import { useState, useCallback } from "react";
import { adminService } from "@b2b/services";
import { SellerStatus } from "@b2b/services";
import { PendingUser } from "../types/user.types";

interface UseUserActionsOptions {
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

export function useUserActions(options: UseUserActionsOptions = {}) {
  const { onSuccess, onError } = options;
  const [loading, setLoading] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const showToast = useCallback((type: "success" | "error" | "info", message: string) => {
    if (type === "error" && onError) {
      onError(message);
    }
  }, [onError]);

  const approveUser = useCallback(async (userId: string, user?: PendingUser) => {
    try {
      setLoading(userId);
      // If user is a seller, use updateSellerStatus
      if (user && (user.role === "SELLER" || user.role === "BUYER_SELLER")) {
        await adminService.updateSellerStatus(userId, SellerStatus.APPROVED);
      } else {
        await adminService.approveUser(userId);
      }
      showToast("success", "User approved successfully");
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error approving user:", err);
      showToast("error", "Failed to approve user");
      return false;
    } finally {
      setLoading(null);
    }
  }, [showToast, onSuccess]);

  const rejectUser = useCallback(async (userId: string, user?: PendingUser) => {
    try {
      setLoading(userId);
      // If user is a seller, use updateSellerStatus
      if (user && (user.role === "SELLER" || user.role === "BUYER_SELLER")) {
        await adminService.updateSellerStatus(userId, SellerStatus.REJECTED);
      } else {
        await adminService.rejectUser(userId);
      }
      showToast("success", "User rejected successfully");
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error rejecting user:", err);
      showToast("error", "Failed to reject user");
      return false;
    } finally {
      setLoading(null);
    }
  }, [showToast, onSuccess]);

  const activateUser = useCallback(async (userId: string) => {
    try {
      setLoading(userId);
      await adminService.activateUser(userId);
      showToast("success", "User activated successfully");
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error activating user:", err);
      showToast("error", "Failed to activate user");
      return false;
    } finally {
      setLoading(null);
    }
  }, [showToast, onSuccess]);

  const suspendUser = useCallback(async (userId: string) => {
    try {
      setLoading(userId);
      await adminService.suspendUser(userId);
      showToast("success", "User suspended successfully");
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error suspending user:", err);
      showToast("error", "Failed to suspend user");
      return false;
    } finally {
      setLoading(null);
    }
  }, [showToast, onSuccess]);

  const deleteUser = useCallback(async (userId: string) => {
    if (!confirm("Are you sure you want to delete this user?")) return false;
    try {
      setLoading(userId);
      await adminService.deleteUser(userId);
      showToast("success", "User deleted successfully");
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error deleting user:", err);
      showToast("error", "Failed to delete user");
      return false;
    } finally {
      setLoading(null);
    }
  }, [showToast, onSuccess]);

  const bulkApprove = useCallback(async (userIds: string[], users: PendingUser[]) => {
    try {
      setBulkLoading(true);
      const sellerIds = users
        .filter(u => (u.role === "SELLER" || u.role === "BUYER_SELLER") && userIds.includes(u.id))
        .map(u => u.id);
      const regularUserIds = userIds.filter(id => !sellerIds.includes(id));

      const promises = [
        ...sellerIds.map(id => adminService.updateSellerStatus(id, SellerStatus.APPROVED)),
        ...regularUserIds.map(id => adminService.approveUser(id)),
      ];

      await Promise.all(promises);
      showToast("success", `${userIds.length} users approved successfully`);
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error bulk approving users:", err);
      showToast("error", "Failed to approve users");
      return false;
    } finally {
      setBulkLoading(false);
    }
  }, [showToast, onSuccess]);

  const bulkReject = useCallback(async (userIds: string[], users: PendingUser[]) => {
    try {
      setBulkLoading(true);
      const sellerIds = users
        .filter(u => (u.role === "SELLER" || u.role === "BUYER_SELLER") && userIds.includes(u.id))
        .map(u => u.id);
      const regularUserIds = userIds.filter(id => !sellerIds.includes(id));

      const promises = [
        ...sellerIds.map(id => adminService.updateSellerStatus(id, SellerStatus.REJECTED)),
        ...regularUserIds.map(id => adminService.rejectUser(id)),
      ];

      await Promise.all(promises);
      showToast("success", `${userIds.length} users rejected successfully`);
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error bulk rejecting users:", err);
      showToast("error", "Failed to reject users");
      return false;
    } finally {
      setBulkLoading(false);
    }
  }, [showToast, onSuccess]);

  const bulkActivate = useCallback(async (userIds: string[]) => {
    try {
      setBulkLoading(true);
      await adminService.bulkUserAction(userIds, "activate");
      showToast("success", `${userIds.length} users activated successfully`);
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error bulk activating users:", err);
      showToast("error", "Failed to activate users");
      return false;
    } finally {
      setBulkLoading(false);
    }
  }, [showToast, onSuccess]);

  const bulkSuspend = useCallback(async (userIds: string[]) => {
    try {
      setBulkLoading(true);
      await adminService.bulkUserAction(userIds, "suspend");
      showToast("success", `${userIds.length} users suspended successfully`);
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error bulk suspending users:", err);
      showToast("error", "Failed to suspend users");
      return false;
    } finally {
      setBulkLoading(false);
    }
  }, [showToast, onSuccess]);

  const bulkDelete = useCallback(async (userIds: string[]) => {
    if (!confirm(`Are you sure you want to delete ${userIds.length} users?`)) return false;
    try {
      setBulkLoading(true);
      await adminService.bulkUserAction(userIds, "delete");
      showToast("success", `${userIds.length} users deleted successfully`);
      onSuccess?.();
      return true;
    } catch (err) {
      console.error("Error bulk deleting users:", err);
      showToast("error", "Failed to delete users");
      return false;
    } finally {
      setBulkLoading(false);
    }
  }, [showToast, onSuccess]);

  return {
    loading,
    bulkLoading,
    approveUser,
    rejectUser,
    activateUser,
    suspendUser,
    deleteUser,
    bulkApprove,
    bulkReject,
    bulkActivate,
    bulkSuspend,
    bulkDelete,
  };
}

